import type { ApexOptions } from 'apexcharts'
import type { ChartSeriesItem, ValueFormatter } from '../types/chart.types'
import { ChartStyleColors } from './chart-colors'
import { compactNumberFormatter, localeNumberFormatter } from './chart-formatters'
import { createStandardTooltip } from './chart-tooltip'

export interface LineChartConfig {
  series: ChartSeriesItem[]
  categories: string[]
  height?: number
  yAxisFormatter?: ValueFormatter
  tooltipValueFormatter?: ValueFormatter
  tooltipHeaderFormatter?: (label: string) => string
}

const getBaseLineChartOptions = (): ApexOptions => ({
  chart: {
    type: 'line',
    height: 350,
    toolbar: { show: false },
    fontFamily: 'inherit',
    animations: {
      enabled: true,
      speed: 300,
      dynamicAnimation: { enabled: true, speed: 300 }
    },
    zoom: { enabled: false }
  },
  dataLabels: { enabled: false },
  legend: { show: false },
  stroke: {
    curve: 'straight'
  },
  xaxis: {
    labels: {
      style: { fontSize: '12px', colors: ChartStyleColors.labelColor }
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
    tooltip: { enabled: false }
  },
  yaxis: {
    labels: {
      style: { fontSize: '12px', colors: ChartStyleColors.labelColor }
    }
  },
  grid: {
    borderColor: ChartStyleColors.gridColor,
    strokeDashArray: 4,
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } }
  },
  tooltip: {
    shared: true,
    intersect: false,
    followCursor: false
  }
})

export const buildLineChartOptions = (config: LineChartConfig): ApexOptions => {
  const {
    series,
    categories,
    height = 350,
    yAxisFormatter = compactNumberFormatter,
    tooltipValueFormatter = localeNumberFormatter,
    tooltipHeaderFormatter
  } = config

  const baseOptions = getBaseLineChartOptions()

  const apexSeries = series.map((s) => ({
    name: s.name,
    data: s.data,
    color: s.color
  }))

  const colors = series.map((s) => s.color)

  return {
    ...baseOptions,
    chart: {
      ...baseOptions.chart,
      height
    },
    series: apexSeries,
    colors,
    xaxis: {
      ...baseOptions.xaxis,
      categories
    },
    yaxis: {
      ...baseOptions.yaxis,
      labels: {
        ...(baseOptions.yaxis as ApexYAxis).labels,
        formatter: yAxisFormatter
      }
    },
    stroke: {
      ...baseOptions.stroke,
      width: series.map(() => 2)
    },
    markers: {
      size: series.map(() => 4),
      strokeWidth: 0,
      colors,
      hover: { size: 6 }
    },
    tooltip: {
      ...baseOptions.tooltip,
      custom: createStandardTooltip({
        valueFormatter: (value) => tooltipValueFormatter(value, 0),
        headerFormatter: tooltipHeaderFormatter
      })
    }
  }
}

export interface DatetimeLineChartConfig extends Omit<LineChartConfig, 'categories'> {
  dates: string[]
}

/** Header label for datetime x-axis tooltips: Apex passes the point's timestamp (or raw label) */
export const datetimeTooltipHeaderFormatter = (label: string): string => {
  const timestamp = Number(label)
  const date = Number.isFinite(timestamp) ? new Date(timestamp) : new Date(label)
  return date.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  })
}

export const buildDatetimeLineChartOptions = (config: DatetimeLineChartConfig): ApexOptions => {
  const {
    series,
    dates,
    height = 350,
    yAxisFormatter = compactNumberFormatter,
    tooltipValueFormatter = localeNumberFormatter
  } = config

  const baseOptions = buildLineChartOptions({
    series,
    categories: dates,
    height,
    yAxisFormatter,
    tooltipValueFormatter,
    tooltipHeaderFormatter: datetimeTooltipHeaderFormatter
  })

  return {
    ...baseOptions,
    xaxis: {
      ...baseOptions.xaxis,
      type: 'datetime',
      labels: {
        ...(baseOptions.xaxis as ApexXAxis).labels,
        format: 'MMM dd'
      }
    }
  }
}
